// redux/slices/phoneModelSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService from "../../services/apiServices";


// Define the initial state
const initialState = {
  phoneModels: [],
  loading: false,
  error: null,
};

// Async thunks for CRUD operations
export const fetchPhoneModels = createAsyncThunk(
  "phoneModels/fetchPhoneModels",
  async (params = {}) => {
    try {
      const { page, limit } = params;
      let url = "phone-models";

      // Use pagination endpoint if page/limit params exist
      if (page && limit) {
        url = `phone-models/pagination?page=${page}&limit=${limit}`;
      }

      const response = await apiService.get(url);

      // If server returns { data: [...], pagination: {...} }, we might need to handle it.
      // But standard reducer expects array or payload.
      // If paginated, response.data might be { data: [], pagination: {} }
      // The current reducer expects array in action.payload.
      // We need to normalize this or update reducer.
      // For now, let's look at what the backend returns:
      // Paginated: { success: true, data: [...], pagination: {...} }
      // Non-Paginated: { success: true, data: [...] }

      return response.data; // Return the full response object to let reducer handle structure
    } catch (error) {
      throw error;
    }
  }
);

export const createPhoneModel = createAsyncThunk(
  "phoneModels/createPhoneModel",
  async (phoneModelData) => {
    try {
      const response = await apiService.post('phone-models', phoneModelData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

export const updatePhoneModel = createAsyncThunk(
  "phoneModels/updatePhoneModel",
  async ({ id, phoneModelData }) => {
    try {

      const response = await apiService.put(`phone-models/${id}`, phoneModelData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

export const deletePhoneModel = createAsyncThunk(
  "phoneModels/deletePhoneModel",
  async (id) => {
    try {
      await apiService.delete(`phone-models/${id}`);
      return id;
    } catch (error) {
      throw error;
    }
  }
);

// Create the slice
const phoneModelSlice = createSlice({
  name: "phoneModels",
  initialState: {
    ...initialState,
    pagination: {
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 1
    }
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPhoneModels.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPhoneModels.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both paginated and non-paginated responses
        if (action.payload.data && Array.isArray(action.payload.data)) {
          state.phoneModels = action.payload.data;
          // If pagination info exists in payload (from server-side endpoint)
          if (action.payload.pagination) {
            state.pagination = action.payload.pagination;
          }
        } else if (Array.isArray(action.payload)) {
          // Fallback for direct array response (if any)
          state.phoneModels = action.payload;
        }
      })
      .addCase(fetchPhoneModels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(createPhoneModel.fulfilled, (state, action) => {
        // Optimistically add to list if not paginated, or refresh
        state.phoneModels.unshift(action.payload.data || action.payload);
        // Note: With pagination, we usually re-fetch, but unshift is fine for immediate feedback
      })
      .addCase(updatePhoneModel.fulfilled, (state, action) => {
        const updatedItem = action.payload.data || action.payload;
        const index = state.phoneModels.findIndex(
          (phoneModel) => phoneModel._id === updatedItem._id || phoneModel.id === updatedItem.id
        );
        if (index !== -1) {
          state.phoneModels[index] = updatedItem;
        }
      })
      .addCase(deletePhoneModel.fulfilled, (state, action) => {
        state.phoneModels = state.phoneModels.filter(
          (phoneModel) => phoneModel._id !== action.payload && phoneModel.id !== action.payload
        );
      });
  },
});

// Export the reducer and actions
export default phoneModelSlice.reducer;

